import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  title: '',
  message: '',
  active: true,
  start_date: '',
  end_date: '',
};

const NoticeManager = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchNotices = async () => {
    try {
      const res = await axios.get(`${API}/notices`);
      setNotices(res.data);
    } catch (e) {
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message) {
      toast.error('Please fill in title and message');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        message: form.message,
        active: form.active,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      await axios.post(`${API}/notices`, payload);
      toast.success('Notice posted');
      closeForm();
      await fetchNotices();
    } catch (err) {
      toast.error('Failed to post notice');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (notice) => {
    try {
      await axios.patch(`${API}/notices/${notice.notice_id}?active=${!notice.active}`);
      toast.success(notice.active ? 'Notice hidden' : 'Notice shown');
      await fetchNotices();
    } catch {
      toast.error('Failed to update notice');
    }
  };

  const handleDelete = async (notice) => {
    if (!window.confirm(`Delete notice "${notice.title}"?`)) return;
    try {
      await axios.delete(`${API}/notices/${notice.notice_id}`);
      toast.success('Notice deleted');
      await fetchNotices();
    } catch {
      toast.error('Failed to delete notice');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E5E5] p-8 text-center text-sm font-body text-[#737373]">
        Loading notices...
      </div>
    );
  }

  return (
    <div data-testid="notice-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-heading font-medium text-[#A0451E]">Holiday & Office Notices</h3>
          <p className="text-sm font-body text-[#737373]">Post a temporary banner shown across the site.</p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="new-notice-btn"
        >
          <Plus className="w-4 h-4" />
          New Notice
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FDF8F5] border-b border-[#E5E5E5]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Title</th>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Dates</th>
                <th className="px-6 py-4 text-left text-sm font-body font-semibold text-[#A0451E]">Status</th>
                <th className="px-6 py-4 text-right text-sm font-body font-semibold text-[#A0451E]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {notices.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm font-body text-[#737373]">
                    No notices yet. Post one to alert visitors about a holiday closure.
                  </td>
                </tr>
              ) : (
                notices.map((notice, index) => (
                  <tr
                    key={notice.notice_id}
                    className="border-b border-[#E5E5E5] hover:bg-[#FDF8F5]"
                    data-testid={`notice-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <p className="text-sm font-body font-semibold text-[#A0451E]">{notice.title}</p>
                      <p className="text-xs font-body text-[#737373] line-clamp-2 max-w-md">{notice.message}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-body text-[#4A4A4A]">
                        {notice.start_date || 'Immediate'} → {notice.end_date || 'Ongoing'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {notice.active ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-body border border-green-300 bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-body border border-gray-300 bg-gray-100 text-gray-700">
                          Hidden
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => toggleActive(notice)}
                          className="p-2 rounded-md hover:bg-[#FDF8F5] text-[#A0451E]"
                          title={notice.active ? 'Hide' : 'Show'}
                          data-testid={`notice-toggle-${index}`}
                        >
                          {notice.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(notice)}
                          className="p-2 rounded-md hover:bg-red-50 text-red-600"
                          title="Delete"
                          data-testid={`notice-delete-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          data-testid="notice-form-modal"
        >
          <div className="bg-white rounded-2xl border border-[#E5E5E5] w-full max-w-xl shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
              <h3 className="text-2xl font-heading font-medium text-[#A0451E]">New Notice</h3>
              <button
                onClick={closeForm}
                className="p-2 rounded-md hover:bg-[#FDF8F5] text-[#4A4A4A]"
                data-testid="notice-form-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g., Diwali Holiday"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="notice-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Our office will be closed on 12-Nov and 13-Nov. We resume regular hours from 14-Nov."
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="notice-message-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">Start date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                    data-testid="notice-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">End date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                    data-testid="notice-end-date"
                  />
                </div>
              </div>
              <p className="text-xs font-body text-[#737373]">
                Leave dates blank to show immediately with no end. If provided, the banner appears only within this range.
              </p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                  className="w-4 h-4"
                  data-testid="notice-active-checkbox"
                />
                <span className="text-sm font-body text-[#4A4A4A]">Show on site immediately</span>
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-3 rounded-full font-semibold border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FDF8F5] font-body"
                  data-testid="notice-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="notice-form-save"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Posting...' : 'Post Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeManager;
